import React, { useState, useEffect, useRef } from 'react';
import SearchBar from '../../../../../shared/search-bar/Search-Bar';
import {
  ACTIVITY_CALENDAR_CATEGORY_CREATE,
  ACTIVITY_CALENDAR_CATEGORY_EDIT,
  ACTIVITY_CALENDAR_CATEGORY_VIEW,
} from '../../../../../constants/ActivityCalendarConst';
import LinearProgressBar from '../../../../../shared/progress-bar/Progress';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Menu,
  MenuItem,
  TableFooter,
  TablePagination,
} from '@material-ui/core';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import CreateIcon from '@material-ui/icons/Create';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { AddCircle, RemoveCircle } from '@material-ui/icons';
import useOutsideAlerter from '../../../../../custom-hooks/useOutsideAlerter';
import DeletePopupModel from '../../../../../shared/delete-popup/Delete-Popup';
import { useSelector, useDispatch } from 'react-redux';
import {
  allActivityCalendarCategoriesSelector,
  fetchAllActivityCategories,
  setActivityCategoriesSuccess,
} from '../../../cmsSlice';
import { notificationManager } from '../../../../../utils/NotificationUtils';
import { decryptLocalStorage } from '../../../../../utils/authUtils';
import { createDataSortMapping } from '../../../../../utils/PropertyAPIUtils';
import { handleOnDragEnd } from '../../../../../shared/custom-dragdrop/dragDrop';
import TablePaginationActions from '../../../../../shared/table-pagination/TablePaginationActions';
import { DragIndicator, MoreHoriz } from '@material-ui/icons';
import {
  DeleteActivityCategoryAPI,
  UpdateActivityCategoryStatusAPI,
} from '../../../../../utils/activityCalendarAPIUtils';
import {
  RBAC_PERMISSION_LEVEL_FULL_ACCESS,
  RBAC_TYPE_CMS_ACTIVITYCALENDAR,
} from '../../../../../constants/RoleConsts';
import { CONFIRM_DELETE_TITLE } from '../../../../../utils/Constants';
import { checkUsageInCollection, DELETE_MESSAGES } from '../../../../../constants/AmenityConst';
import { retrieveNoOfActivities } from '../../../../../utils/commonUtils';
import WarningPopup from '../../../../../shared/warning-popup/Warning-Popup';

const ActivityCalendarCategoryList = (props) => {
  const {
    onDrawerToggle = () => {},
    onViewToggle,
    selectedHotel,
    permissions,
    page,
    rowsPerPage,
    onsetRowsPerPage,
    onsetPage,
  } = props;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [searchText, setSearchText] = useState('');
  const dragDisabled = !!searchText || categories?.length <= 1;
  const [selectedValue, setSelectedValue] = useState({
    id: '',
    name: '',
    title: '',
    description: '',
    status: false,
    anchorEl: null,
  });
  const wrapperRef = useRef(null);
  const prevHotelIdRef = useRef(selectedHotel);
  const [popUp, setPopUp] = useState(false);
  const [visible, setVisible] = useState(false);
  const originalCategories = useSelector(allActivityCalendarCategoriesSelector);
  const getAllActivities = useSelector(({ cms }) => cms?.activityList);

  useEffect(() => {
    setCategories(originalCategories);
    setLoading(false);
    setSearchText('');
  }, [originalCategories]);

  useEffect(() => {
    if (loading || categories?.length === 0) return;
    const totalItems = categories?.length;
    const maxPageIndex = Math.max(Math.ceil(totalItems / rowsPerPage) - 1, 0);
    if (page > maxPageIndex) {
      onsetPage(maxPageIndex);
    }
  }, [categories?.length, rowsPerPage, page, onsetPage]);

  useEffect(() => {
    if (prevHotelIdRef?.current !== selectedHotel) {
      onsetPage(0);
    }
    prevHotelIdRef.current = selectedHotel;
  }, [selectedHotel]);

  const handleSearchText = (searchText) => {
    setSearchText(searchText);
    const filteredRows = originalCategories?.filter((item) => {
      return item?.name?.toLowerCase().includes(searchText?.toLowerCase());
    });
    onsetPage(0);
    setCategories(filteredRows);
  };

  const handleChangePage = (event, newPage) => {
    onsetPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    onsetRowsPerPage(parseInt(event?.target?.value, 10));
    onsetPage(0);
  };

  const onOpenDrawer = (event, type) => {
    event.stopPropagation();
    onDrawerToggle(type, true, selectedValue?.category);
    handleClose();
  };

  const handleClose = () => {
    setSelectedValue({
      id: '',
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: null,
      category: null,
    });
  };

  const handleCellMenuClick = (event, category) => {
    event.stopPropagation();
    setSelectedValue({
      id: category?.id,
      name: category?.name,
      title: '',
      description: '',
      status: false,
      anchorEl: event?.currentTarget,
      category: category,
    });
  };

  const onActiveDeactive = async (e, currentStatus, category) => {
    e.stopPropagation();
    const lang = decryptLocalStorage('lang');
    const language = lang !== 'en' ? lang : '';
    try {
      const res = await UpdateActivityCategoryStatusAPI(selectedHotel, category?.id, language);
      if (res?.data?.updateActivityCategoryStatus?.status) {
        dispatch(fetchAllActivityCategories(selectedHotel));
        notificationManager(
          'success',
          res?.data?.updateActivityCategoryStatus?.message ||
            'Category status updated successfully',
        );
        handleClose();
      } else {
        notificationManager('danger', 'Failed to update category status');
      }
    } catch (error) {
      if (error?.graphQLErrors) {
        notificationManager('danger', error?.graphQLErrors[0]?.message);
      } else {
        notificationManager('danger', 'Error updating category status');
      }
    }
  };

  const checkUsageInActivities = () => {
    return checkUsageInCollection(getAllActivities, 'categoryId', selectedValue);
  };

  const deleteActivityCalendarCategory = async (e) => {
    e.preventDefault();
    if (checkUsageInActivities()) {
      setSelectedValue((prev) => ({
        ...prev,
        title: CONFIRM_DELETE_TITLE,
        description: DELETE_MESSAGES?.activityCategory?.cannotDelete,
        status: false,
        warn: true,
        anchorEl: null,
      }));
    } else {
      setSelectedValue((prev) => ({
        ...prev,
        title: CONFIRM_DELETE_TITLE,
        description: DELETE_MESSAGES?.activityCategory?.irreversible,
        status: true,
        warn: false,
        anchorEl: null,
      }));
    }
  };

  const deleteCategory = async () => {
    try {
      if (selectedHotel && selectedValue && selectedValue.id) {
        const lang = decryptLocalStorage('lang');
        const language = lang !== 'en' ? lang : '';
        const res = await DeleteActivityCategoryAPI(selectedHotel, selectedValue.id, language);
        if (res?.data?.deleteActivityCategory?.status === true) {
          dispatch(fetchAllActivityCategories(selectedHotel));
          notificationManager('success', 'Activity category deleted successfully');
          handleClose();
        } else {
          notificationManager('danger', 'Failed to delete activity category');
          handleClose();
        }
      }
    } catch (e) {
      if (e?.graphQLErrors) {
        notificationManager('danger', e?.graphQLErrors[0]?.message);
      } else {
        notificationManager('danger', 'Failed to delete activity category');
      }
      handleClose();
    }
  };

  const onDeleteCancel = () => {
    setSelectedValue({
      id: '',
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: null,
      category: null,
    });
  };

  const onViewDetails = (category, e) => {
    if (e?.target?.tagName === 'TD') {
      onViewToggle(ACTIVITY_CALENDAR_CATEGORY_VIEW, true, category);
    }
  };

  const onClickView = (e) => {
    if (selectedValue && selectedValue?.category) {
      onViewToggle(ACTIVITY_CALENDAR_CATEGORY_VIEW, true, selectedValue?.category);
    }
  };

  useOutsideAlerter(wrapperRef, visible, setPopUp);

  return (
    <div className='card shadow'>
      <div className='mt-4 mb-4 group-list-container'>
        {selectedValue?.id && selectedValue?.status === true && (
          <DeletePopupModel
            deletePopUp={selectedValue}
            onDelete={deleteCategory}
            onDeleteCancel={onDeleteCancel}
          />
        )}
        {selectedValue?.id && selectedValue?.warn === true && (
          <WarningPopup
            isTitleNameShow={false}
            warningPopup={selectedValue}
            onWarningClose={handleClose}
            warn={true}
          />
        )}
        <SearchBar
          type={ACTIVITY_CALENDAR_CATEGORY_CREATE}
          displayAddOrEdit={
            permissions[RBAC_TYPE_CMS_ACTIVITYCALENDAR] === RBAC_PERMISSION_LEVEL_FULL_ACCESS
              ? 'show'
              : 'hide'
          }
          buttonName='Add Category'
          setSearchText={handleSearchText}
          onOpenDrawer={onOpenDrawer}
          popUp={popUp}
          setPopUp={setPopUp}
          placeHolder={categories}
          setPlaceHolder={setCategories}
          globalPlaceHolder={originalCategories}
          setGlobalPlaceHolder={setActivityCategoriesSuccess}
          dispatch={dispatch}
          mappingApi={createDataSortMapping}
          displayText='Activity category'
          module='ACTIVITY_CATEGORY'
          searchText={searchText}
          setSearch={setSearchText}
          actionMenuItems={[]}
          visible={visible}
          setVisible={setVisible}
        />
        <div ref={wrapperRef} className='row mx-2 d-flex mt-4'>
          {loading ? (
            <LinearProgressBar />
          ) : (
            categories && (
              <TableContainer>
                <Table className='list-table' aria-label='simple table'>
                  <TableHead>
                    <TableRow>
                      <TableCell> </TableCell>
                      <TableCell className='w-40'>Name</TableCell>
                      <TableCell className='w-30'>Category Code</TableCell>
                      <TableCell className='w-50' align='center'>
                        No.of Activities
                      </TableCell>
                      <TableCell className='w-60' align='center'>
                        Status
                      </TableCell>
                      <TableCell align='right'></TableCell>
                    </TableRow>
                  </TableHead>

                  <DragDropContext
                    onDragEnd={(event) =>
                      handleOnDragEnd(
                        event,
                        setVisible,
                        categories,
                        setCategories,
                        false,
                        page,
                        rowsPerPage,
                      )
                    }
                    isDropDisabled={dragDisabled}
                  >
                    <Droppable droppableId='spas' isDropDisabled={dragDisabled}>
                      {(provided) => (
                        <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                          {(rowsPerPage > 0
                            ? categories.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            : categories
                          ).map((category, i) => (
                            <Draggable
                              key={category?.id}
                              draggableId={category?.id}
                              index={i}
                              isDragDisabled={dragDisabled}
                            >
                              {(provided) => (
                                <TableRow
                                  key={i}
                                  className='border-white pointer'
                                  onClick={(e) => {
                                    onViewDetails(category, e);
                                  }}
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                >
                                  <TableCell>
                                    {!dragDisabled && (
                                      <DragIndicator className='res-menu-accordion-drag' />
                                    )}
                                  </TableCell>
                                  <TableCell className='w-40'>{category?.name}</TableCell>
                                  <TableCell className='w-30'>{category?.categoryCode}</TableCell>
                                  <TableCell className='w-50' align='center'>
                                    {retrieveNoOfActivities(category, getAllActivities)}
                                  </TableCell>
                                  <TableCell className='w-60' align='center'>
                                    <div className='d-flex justify-content-center'>
                                      <div
                                        className={`pill d-flex justify-content-center align-items-center ${
                                          category?.isActive
                                            ? 'pill-active'
                                            : 'pill-inactive theme-dark-green'
                                        }`}
                                      >
                                        <span className='ms-1'>
                                          {category?.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell align='right'>
                                    {permissions[RBAC_TYPE_CMS_ACTIVITYCALENDAR] ===
                                      RBAC_PERMISSION_LEVEL_FULL_ACCESS && (
                                      <Button
                                        aria-controls='simple-menu'
                                        aria-haspopup='true'
                                        onClick={(e) => {
                                          handleCellMenuClick(e, category);
                                        }}
                                        className='showpopupInner'
                                      >
                                        <MoreHoriz className='showpopupInner' />
                                      </Button>
                                    )}
                                    <Menu
                                      id={i}
                                      anchorEl={selectedValue?.anchorEl}
                                      keepMounted
                                      open={
                                        Boolean(selectedValue?.anchorEl) &&
                                        selectedValue?.id === category?.id
                                      }
                                      onClose={handleClose}
                                      className='shadow'
                                    >
                                      <MenuItem
                                        className='pe-4'
                                        onClick={(e) =>
                                          onActiveDeactive(e, category?.isActive, category)
                                        }
                                      >
                                        {category?.isActive ? (
                                          <>
                                            <RemoveCircle className='me-2 action-menu-icon' />
                                            Deactivate
                                          </>
                                        ) : (
                                          <>
                                            <AddCircle className='me-2 action-menu-icon' />
                                            Activate
                                          </>
                                        )}
                                      </MenuItem>
                                      <MenuItem
                                        className='pe-4'
                                        onClick={(e) => {
                                          onOpenDrawer(e, ACTIVITY_CALENDAR_CATEGORY_EDIT);
                                        }}
                                      >
                                        <CreateIcon className='me-2 action-menu-icon' />
                                        Edit
                                      </MenuItem>
                                      <MenuItem
                                        className='pe-4'
                                        onClick={(e) => {
                                          onClickView(e);
                                        }}
                                      >
                                        <VisibilityIcon className='me-2 action-menu-icon' />
                                        View
                                      </MenuItem>
                                      <MenuItem
                                        className='pe-4'
                                        onClick={(e) => deleteActivityCalendarCategory(e)}
                                      >
                                        <DeleteForeverIcon className='me-2 action-menu-icon' />
                                        Delete
                                      </MenuItem>
                                    </Menu>
                                  </TableCell>
                                </TableRow>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </TableBody>
                      )}
                    </Droppable>
                  </DragDropContext>
                  <TableFooter>
                    <TableRow className='paginator'>
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                        count={categories?.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        SelectProps={{
                          inputProps: { 'aria-label': 'rows per page' },
                          native: true,
                        }}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        ActionsComponent={TablePaginationActions}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityCalendarCategoryList;
